import React, { useCallback, useEffect, useState } from "react";
import DataBase from "../../dataBase/liquidGuides";
import { groupReports } from "../../Common/shareInvites";

// Admin Dashboard "Reports" panel (4.10.55, 2026-10-26): guides people
// reported with "Report this guide". Apple expects reports to be acted on
// promptly (about a day). Open the guide to look; take action yourself
// (e.g. delete or unshare it in the Firebase console, contact the owner),
// then mark the reports Actioned or Dismissed.
// 2026-10-29: one row per guide (open first, most reported first) with every
// report under it, and "all" buttons that close the guide's open reports at
// once. A reporter who has filed many reports is marked, so a flood from one
// person stands out (each person can report a guide only once — Phase 4 rules).

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "");
const MANY = 5; // reports by one person that get them marked

const GuideReportsPanel = () => {
  const [reports, setReports] = useState(null);
  const [error, setError] = useState("");
  const [showClosed, setShowClosed] = useState(false);
  const [open, setOpen] = useState({}); // guide_info_id -> expanded

  const load = useCallback(() => {
    DataBase.getGuideReports((res) => {
      if (res.pass !== true) {
        setError("Couldn't load reports — are the Phase 3 Firestore rules published?");
        setReports([]);
        return;
      }
      setError("");
      setReports(res.res);
    });
  }, []);

  useEffect(load, [load]);

  const close = (list, status) => {
    const closed = new Date().toISOString();
    list.forEach((r) =>
      DataBase.updateGuideReport(r.id, { status, closed }, (res) => {
        if (res.pass === true) setReports((all) => all.map((x) => (x.id === r.id ? { ...x, status } : x)));
      })
    );
  };

  if (reports === null) return <p style={{ padding: 12 }}>Loading reports…</p>;
  const { rows, perReporter } = groupReports(reports);
  const shown = rows.filter((g) => showClosed || g.open > 0);
  const openCount = reports.filter((r) => r.status === "open").length;

  return (
    <div style={{ padding: "8px 4px" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
        <strong>
          {openCount} open {openCount === 1 ? "report" : "reports"} · {rows.filter((g) => g.open > 0).length} guides
        </strong>
        <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
          <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
          Show closed
        </label>
        <button className="tai-cancel-btn" onClick={load}>Refresh</button>
      </div>
      {error && <p style={{ color: "#B91C1C" }}>{error}</p>}
      {shown.length === 0 && !error && <p>No reports.</p>}
      {shown.map((g) => {
        const key = g.guide_info_id;
        const openOnes = g.reports.filter((r) => r.status === "open");
        const list = g.reports.filter((r) => showClosed || r.status === "open");
        return (
          <div
            key={key}
            className="grp-guide"
            style={{ border: "1px solid #E2E8F0", borderRadius: 10, padding: 10, marginBottom: 8, background: g.open ? "#FFF7ED" : "#FFFFFF" }}
          >
            <div style={{ fontWeight: 600 }}>
              {g.guide_name || "(no name)"}{" "}
              <span style={{ fontWeight: 400, color: g.reports.length > 1 ? "#B91C1C" : "#64748B" }}>
                — {g.reports.length === 1 ? "1 report" : `${g.reports.length} reports`}
                {g.open !== g.reports.length ? ` (${g.open} open)` : ""}
              </span>
            </div>
            <div style={{ fontSize: 13, color: "#475569" }}>
              owner {g.owner} · {g.reasons.join(", ")} · latest {fmt(g.latest)}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap", alignItems: "center" }}>
              <a href={`https://andiamoplaces.com?id=${g.guide_info_id}`} target="_blank" rel="noreferrer">Open guide</a>
              {openOnes.length > 0 && (
                <>
                  <button className="tai-cancel-btn" onClick={() => close(openOnes, "actioned")}>
                    {openOnes.length > 1 ? "Actioned (all)" : "Actioned"}
                  </button>
                  <button className="tai-cancel-btn" onClick={() => close(openOnes, "dismissed")}>
                    {openOnes.length > 1 ? "Dismiss all" : "Dismiss"}
                  </button>
                </>
              )}
              <button className="tai-cancel-btn" onClick={() => setOpen({ ...open, [key]: !open[key] })} aria-expanded={!!open[key]}>
                {open[key] ? "Hide reports" : "Show reports"}
              </button>
            </div>
            {open[key] && (
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                {list.map((r) => (
                  <div key={r.id} className="grp-report" style={{ borderTop: "1px solid #E2E8F0", paddingTop: 6, fontSize: 13 }}>
                    <div>
                      <b>{r.reason}</b> · {fmt(r.created)} · by {r.reporter_email || r.reporter_uid}
                      {perReporter[r.reporter_uid] >= MANY && (
                        <span style={{ color: "#B91C1C" }}> · has filed {perReporter[r.reporter_uid]} reports</span>
                      )}{" "}
                      · {r.status}
                    </div>
                    {r.details && <div style={{ whiteSpace: "pre-wrap", margin: "2px 0" }}>{r.details}</div>}
                    {r.status === "open" && openOnes.length > 1 && (
                      <div style={{ display: "flex", gap: 8 }}>
                        <button className="tai-cancel-btn" onClick={() => close([r], "actioned")}>Actioned</button>
                        <button className="tai-cancel-btn" onClick={() => close([r], "dismissed")}>Dismiss</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default GuideReportsPanel;
